import {
    CrossIcon,
    HamburgerIcon
} from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import LogoShort from "../../assets/logos/solomon-icon.svg";
import { cn } from "../../utils/cn";

export type YaasConsoleKind = "client" | "issuer";

export interface YaasTopBarUser {
    fullName: string | null;
    email: string;
    orgName: string;
    orgRole: string;
    /**
     * Issuer org for this client. Shown in the client-console
     * breadcrumb as YaaS / {issuer} / {client}. Issuer consoles omit.
     */
    issuerName?: string | null;
}

interface YaasTopBarProps {
    consoleKind: YaasConsoleKind;
    /**
     * Fallback org label when `user` isn't loaded yet.
     * Once `user` is present the lockup uses `user.orgName`.
     */
    appLabel: string;
    user?: YaasTopBarUser;
    sidebarCollapsed?: boolean;
    actions?: React.ReactNode;
    /**
     * Mobile only — toggles the off-canvas nav drawer. When provided, a
     * hamburger button renders at the start of the bar (hidden at `md+`,
     * where the in-flow rail is used instead).
     */
    onMenuClick?: () => void;
    /** Whether the mobile drawer is currently open (drives the icon). */
    menuOpen?: boolean;
}

function YaasPlatformMark(): React.ReactElement {
    return (
        <Text
            variant="small"
            render={<span />}
            className="shrink-0 font-bold tracking-[-0.02em] text-foreground-base"
        >
            YaaS
        </Text>
    );
}

/**
 * Icon + YaaS in the sidebar-aligned rail; wordmark omitted.
 *
 * Mobile (`< md`): the in-flow rail doesn't exist, so the lockup sits at
 * its natural width next to the hamburger, with the YaaS mark always
 * shown. Desktop (`md+`): the rail width tracks the sidebar's collapsed
 * state so the lockup stays aligned to the column beneath it.
 */
function TopBarLogoRail({
    sidebarCollapsed
}: {
    sidebarCollapsed: boolean;
}): React.ReactElement {
    return (
        <div
            className={cn(
                "flex h-full shrink-0 items-center gap-2 overflow-hidden transition-[width] duration-200 ease-out",
                "w-auto justify-start pr-3",
                sidebarCollapsed
                    ? "md:w-12 md:justify-center md:pr-0"
                    : "md:w-40 md:justify-start md:px-4"
            )}
        >
            <img
                src={LogoShort}
                alt="Solomon"
                className="h-5 w-auto shrink-0"
            />
            {/* Always visible on mobile; hidden on desktop only when the
                rail is collapsed to its icon width. */}
            <span
                className={cn("inline-flex", sidebarCollapsed && "md:hidden")}
            >
                <YaasPlatformMark />
            </span>
        </div>
    );
}

/** Hamburger / close toggle for the mobile nav drawer. */
function MobileMenuButton({
    onClick,
    open
}: {
    onClick: () => void;
    open: boolean;
}): React.ReactElement {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            className="flex h-full w-11 shrink-0 items-center justify-center text-foreground-muted transition-colors hover:text-foreground-base md:hidden"
        >
            {open ? (
                <CrossIcon className="size-5" />
            ) : (
                <HamburgerIcon className="size-5" />
            )}
        </button>
    );
}

function BreadcrumbSlash(): React.ReactElement {
    return (
        <span
            className="shrink-0 font-sans text-xs leading-none text-foreground-imprint"
            aria-hidden="true"
        >
            /
        </span>
    );
}

/** Issuer console trail: / {org} */
function YaasIssuerLockup({
    orgName
}: {
    orgName: string;
}): React.ReactElement {
    return (
        <div
            className="flex min-w-0 items-center gap-2.5"
            aria-label={`YaaS, ${orgName}`}
        >
            <BreadcrumbSlash />
            <Text
                variant="small"
                className="min-w-0 truncate text-foreground-base"
            >
                {orgName}
            </Text>
        </div>
    );
}

/** Client console trail: / {issuer} / {client} */
function YaasClientLockup({
    issuerName,
    clientName
}: {
    issuerName?: string | null;
    clientName: string;
}): React.ReactElement {
    const ariaLabel = issuerName
        ? `YaaS, ${issuerName}, ${clientName}`
        : `YaaS, ${clientName}`;

    return (
        <div
            className="flex min-w-0 items-center gap-2.5"
            aria-label={ariaLabel}
        >
            <BreadcrumbSlash />
            {issuerName && (
                <>
                    <Text
                        variant="small"
                        className="min-w-0 truncate text-foreground-muted"
                    >
                        {issuerName}
                    </Text>
                    <BreadcrumbSlash />
                </>
            )}
            <Text
                variant="small"
                className="min-w-0 truncate text-foreground-base"
            >
                {clientName}
            </Text>
        </div>
    );
}

export function YaasTopBar({
    consoleKind,
    appLabel,
    user,
    sidebarCollapsed = false,
    actions,
    onMenuClick,
    menuOpen = false
}: YaasTopBarProps): React.ReactElement {
    const orgName = user?.orgName ?? appLabel;

    return (
        <header className="flex h-11 shrink-0 z-10 items-stretch border-b border-line-base bg-surface-ground">
            {onMenuClick && (
                <MobileMenuButton onClick={onMenuClick} open={menuOpen} />
            )}
            <TopBarLogoRail sidebarCollapsed={sidebarCollapsed} />

            <div className="flex min-w-0 flex-1 items-center pl-2 pr-4">
                {consoleKind === "client" ? (
                    <YaasClientLockup
                        issuerName={user?.issuerName}
                        clientName={orgName}
                    />
                ) : (
                    <YaasIssuerLockup orgName={orgName} />
                )}
            </div>

            {actions && (
                <div className="flex shrink-0 items-center gap-2 border-l border-line-base px-4">
                    {actions}
                </div>
            )}
        </header>
    );
}
